import { PlanCell, PlanColumn, PlanProject, PlanRow } from "./plan-types";

export interface SaveUnitCommand {
    hours: number;
    minutes: number;
    inventoryNumber: number | null;
    type: string;
    fromHome: boolean;
    forExpertise: boolean;
    forTrip: boolean;
    comment: string;
    workId: string | null;
}

export interface SaveDayCommand {
    day: string;
    employerId: string;
    comment: string;
    isRemove?: boolean;
    units: SaveUnitCommand[];
}

export interface PlanDiff {
    days: SaveDayCommand[];
    oldDays: SaveDayCommand[];
}

export interface DiffOptions {
    /** Название типа часов «Отработано» */
    workedOutType: string;
    /** Часов в рабочем дне (всегда полный день) */
    workDayHours: number;
}

function sameCell(left: PlanCell | undefined, right: PlanCell | undefined): boolean {
    if (!left || !right) {
        return left === right;
    }

    if (left.kind !== right.kind) {
        return false;
    }

    if (left.kind === "work" && right.kind === "work") {
        return left.orderId === right.orderId;
    }

    // смена типа отсутствия (отпуск -> больничный) — тоже правка, её надо сохранить
    if (left.kind === "absence" && right.kind === "absence") {
        return left.type === right.type;
    }

    return true;
}

function unitOf(project: PlanProject | undefined, options: DiffOptions): SaveUnitCommand {
    return {
        hours: options.workDayHours,
        minutes: 0,
        inventoryNumber: project ? project.inventoryNumber : null,
        type: options.workedOutType,
        fromHome: false,
        forExpertise: false,
        forTrip: false,
        comment: "",
        workId: null,
    };
}

/**
 * День отсутствия: проекта у него нет, тип часов — из справочника (отпуск, больничный,
 * без сохранения). `IsWorkedOut` бэк выводит сам по имени типа.
 */
function absenceUnitOf(type: string, options: DiffOptions): SaveUnitCommand {
    return {
        hours: options.workDayHours,
        minutes: 0,
        inventoryNumber: null,
        type,
        fromHome: false,
        forExpertise: false,
        forTrip: false,
        comment: "",
        workId: null,
    };
}

/**
 * Что изменилось между двумя состояниями плана.
 *
 * `days` — новое состояние изменившихся дней, `oldDays` — их прежнее состояние:
 * бэку прежнее нужно, чтобы пересчитать кэш плановых затрат ещё и по проекту,
 * с которого день сняли.
 */
export function buildDiff(
    before: PlanRow[],
    after: PlanRow[],
    columns: PlanColumn[],
    projects: PlanProject[],
    options: DiffOptions
): PlanDiff {
    const projectsMap = new Map<string, PlanProject>();
    projects.forEach((project) => projectsMap.set(project.id, project));

    const beforeMap = new Map<string, PlanRow>();
    before.forEach((row) => beforeMap.set(row.employerId, row));

    const days: SaveDayCommand[] = [];
    const oldDays: SaveDayCommand[] = [];

    after.forEach((row) => {
        const previous = beforeMap.get(row.employerId);

        if (!previous) {
            return;
        }

        columns.forEach((column, index) => {
            if (column.isPast) {
                return;
            }

            const next = row.cells[index];
            const old = previous.cells[index];

            if (sameCell(old, next)) {
                return;
            }

            if (next && next.kind === "work") {
                days.push({
                    day: column.day,
                    employerId: row.employerId,
                    comment: "",
                    units: [unitOf(projectsMap.get(next.orderId), options)],
                });
            } else if (next && next.kind === "absence") {
                days.push({
                    day: column.day,
                    employerId: row.employerId,
                    comment: "",
                    units: [absenceUnitOf(next.type, options)],
                });
            } else {
                days.push({
                    day: column.day,
                    employerId: row.employerId,
                    comment: "",
                    isRemove: true,
                    units: [],
                });
            }

            if (old && old.kind === "work") {
                oldDays.push({
                    day: column.day,
                    employerId: row.employerId,
                    comment: "",
                    units: [unitOf(projectsMap.get(old.orderId), options)],
                });
            }
        });
    });

    return { days, oldDays };
}
